/* eslint-disable arrow-parens */
/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import ReportService from "@/services/ReportService";
import StatisticsService from "@/services/StatisticsService";
import moment from "moment";

const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

const state = () => ({
  mapData: null,
  lineChartData: null,
  lineChartDataLeasingCompany: null,
  pieData: null,
  transfersPieData: null,
  topCompaniesData: null,
  managedVehicleCount: null,
  unManagedVehicleCount: null,
  unpaidReportCount: null,
  unpaidAmount: null,
  awaitingTransfersCount: null,
});

const getters = {
  mapData: state => state.mapData,
  lineChartData: state => state.lineChartData,
  getPieData: state => state.pieData,
  lineChartDataLeasingCompany: state => state.lineChartDataLeasingCompany,
  getTransfersPieData: state => state.transfersPieData,
  getTopCompaniesData: state => state.topCompaniesData,
};

const mutations = {
  setLineChartDataLeasingCompany(state, value) {
    console.log("value", value);
    state.lineChartDataLeasingCompany = value;
  },
  setLineChartDataLeasingCompanyToStorage(state, value) {
    if (value) {
      const valWithExperation = {
        data: value,
        expired: new Date(Date.now() + CACHE_DURATION_MS)
      };
      const profileId = localStorage.getItem("profile_id");
      localStorage.setItem(`dashboardLineChartDataLeasingCompany-${profileId}`, JSON.stringify(valWithExperation));
    } else {
      localStorage.removeItem("dashboardLineChartDataLeasingCompany");
    }
  },
  setMapData(state, value) {
    state.mapData = value;
  },
  setMapDataToStorage(state, value) {
    if (value) {
      const valWithExperation = {
        data: value,
        expired: new Date(Date.now() + CACHE_DURATION_MS)
      };
      const profileId = localStorage.getItem("profile_id");
      localStorage.setItem(`dashboardMapData-${profileId}`, JSON.stringify(valWithExperation));
    } else {
      localStorage.removeItem("dashboardMapData");
    }
  },
  setLineChartData(state, value) {
    state.lineChartData = value;
  },
  setLineChartDataToStorage(state, value) {
    if (value) {
      const valWithExperation = {
        data: value,
        expired: new Date(Date.now() + CACHE_DURATION_MS)
      };
      const profileId = localStorage.getItem("profile_id");
      localStorage.setItem(
        `dashboardLineChartData-${profileId}`,
        JSON.stringify(valWithExperation)
      );
    } else {
      localStorage.removeItem("dashboardLineChartData");
    }
  },

  setPieData(state, value) {
    state.pieData = value;
  },
  setPieDataToStorage(state, value) {
    if (value) {
      const valWithExperation = {
        data: value,
        expired: new Date(Date.now() + CACHE_DURATION_MS)
      };
      const profileId = localStorage.getItem("profile_id");
      localStorage.setItem(`dashboardPieData-${profileId}`, JSON.stringify(valWithExperation));
    } else {
      localStorage.removeItem("dashboardPieData");
    }
  },
  setTransfersPieData(state, value) {
    state.transfersPieData = value;
  },
  setTransfersPieDataToStorage(state, value) {
    if (value) {
      const valWithExperation = {
        data: value,
        expired: new Date(Date.now() + CACHE_DURATION_MS)
      };
      const profileId = localStorage.getItem("profile_id");
      localStorage.setItem(`dashboardTransfersPieData-${profileId}`, JSON.stringify(valWithExperation));
    } else {
      localStorage.removeItem("dashboardTransfersPieData");
    }
  },
  setTopCompaniesData(state, value) {
    state.topCompaniesData = value;
  },
  setTopCompaniesDataToStorage(state, value) {
    if (value) {
      const valWithExperation = {
        data: value,
        expired: new Date(Date.now() + CACHE_DURATION_MS)
      };
      const profileId = localStorage.getItem("profile_id");
      localStorage.setItem(`dashboardTopCompaniesData-${profileId}`, JSON.stringify(valWithExperation));
    } else {
      localStorage.removeItem("dashboardTopCompaniesData");
    }
  },
  setManagedVehicleCount(state, value) {
    state.managedVehicleCount = value;
  },
  setUnManagedVehicleCount(state, value) {
    state.unManagedVehicleCount = value;
  },
  setUnpaidReportCount(state, value) {
    state.unpaidReportCount = value;
  },
  setUnpaidAmount(state, value) {
    state.unpaidAmount = value;
  },
  setAwaitingTransfersCount(state, value) {
    state.awaitingTransfersCount = value;
  },
  clearCache(state) {
    const localStorageKeys = Object.keys(localStorage);
    const storageArr = ["dashboardMapData", "dashboardLineChartData", "dashboardPieData", "dashboardTopCompaniesData"];
    const filteredArr = [];

    localStorageKeys.forEach(k => {
      storageArr.forEach(str => {
        if (k.includes(str)) filteredArr.push(k);
      });
    });

    state.mapData = null;
    state.lineChartData = null;
    state.pieData = null;
    state.topCompaniesData = null;
    state.managedVehicleCount = null;
    state.unManagedVehicleCount = null;
    state.unpaidReportCount = null;
    state.unpaidAmount = null;
    state.awaitingTransfersCount = null;

    filteredArr.forEach(str => {
      localStorage.removeItem(str);
    });
  }
};

// One-time purge of dashboard cache entries written under the old 7-day expiry.
// Runs once per browser; bump the flag suffix to force another purge in the future.
const CACHE_RESET_FLAG = "dashboardCacheReset_v1";
const purgeOldCacheOnce = () => {
  if (localStorage.getItem(CACHE_RESET_FLAG)) return;
  Object.keys(localStorage)
    .filter(k => k.startsWith("dashboard"))
    .forEach(k => localStorage.removeItem(k));
  localStorage.setItem(CACHE_RESET_FLAG, "1");
};

const actions = {
  loadStorage({ commit }) {
    purgeOldCacheOnce();
    const profileId = localStorage.getItem(`profile_id`);

    const mapData = localStorage.getItem(`dashboardMapData-${profileId}`);
    if (mapData) {
      const parsedMapData = JSON.parse(mapData);
      if (new Date(parsedMapData.expired) >= new Date(Date.now())) {
        commit("setMapData", parsedMapData.data);
      }
    }

    const lineChartData = localStorage.getItem(`dashboardLineChartData-${profileId}`);
    const lineChartDataLeasingCompany = localStorage.getItem(`dashboardLineChartDataLeasingCompany-${profileId}`);
    if (lineChartData) {
      const parsedLineChartData = JSON.parse(lineChartData);
      if (new Date(parsedLineChartData.expired) >= new Date(Date.now())) {
        commit("setLineChartData", parsedLineChartData.data);
      }
    }
    if (lineChartDataLeasingCompany) {
      const parsedLineChartDataLeasingCompany = JSON.parse(lineChartDataLeasingCompany);
      if (new Date(parsedLineChartDataLeasingCompany.expired) >= new Date(Date.now())) {
        commit("setLineChartDataLeasingCompany", parsedLineChartDataLeasingCompany.data);
      }
    }
    const pieData = localStorage.getItem(`dashboardPieData-${profileId}`);
    if (pieData) {
      const parsedPieData = JSON.parse(pieData);
      if (new Date(parsedPieData.expired) >= new Date(Date.now())) {
        commit("setPieData", parsedPieData.data);
      }
    }
    const transfersPieData = localStorage.getItem(`dashboardTransfersPieData-${profileId}`);
    if (transfersPieData) {
      const parsedTransfersPieData = JSON.parse(transfersPieData);
      if (new Date(parsedTransfersPieData.expired) >= new Date(Date.now())) {
        commit("setTransfersPieData", parsedTransfersPieData.data);
      }
    }
    const topCompaniesData = localStorage.getItem(`dashboardTopCompaniesData-${profileId}`);
    if (topCompaniesData) {
      const parsedTopCompaniesData = JSON.parse(topCompaniesData);
      if (new Date(parsedTopCompaniesData.expired) >= new Date(Date.now())) {
        commit("setTopCompaniesData", parsedTopCompaniesData.data);
      }
    }
  },
  getMapData({ commit }) {
    return ReportService.forMap({
      PageSize: 999999,
      FromDateTaken: moment()
        .subtract(1, "year")
        .toDate()
    }).then(res => {
      commit("setMapData", res);
      commit("setMapDataToStorage", res);
      return res;
    });
  },
  getLineChartData({ commit }) {
    return StatisticsService.byMonths().then(res => {
      commit("setLineChartData", res.data);
      commit("setLineChartDataToStorage", res.data);
      return res.data;
    });
  },
  getLineChartDataLeasingCompany({ commit }) {
    return StatisticsService.transfersCompletedByMonths().then(res => {
      console.log("res.data", res.data);
      commit("setLineChartDataLeasingCompany", res.data);
      commit("setLineChartDataLeasingCompanyToStorage", res.data);
      return res.data;
    });
  },
  getPieData({ commit }) {
    return StatisticsService.byStatus(new Date().getFullYear()).then(res => {
      commit("setPieData", res.data);
      commit("setPieDataToStorage", res.data);
      return res.data;
    });
  },
  getTransfersPieData({ commit }) {
    return StatisticsService.transfersPie().then(res => {
      console.log("res.data", res.data);
      commit("setTransfersPieData", res.data);
      commit("setTransfersPieDataToStorage", res.data);
      return res.data;
    }
  ).catch(err => {
    console.log("err", err);
    return [];
  }).finally(() => {
    console.log("finally");
  });
  },
  getTopCompaniesData({ commit }) {
    return StatisticsService.topTransferCompanies().then(res => {
      commit("setTopCompaniesData", res.data);
      commit("setTopCompaniesDataToStorage", res.data);
      return res.data;
    }).catch(err => {
      console.log("err", err);
      return [];
    });
  },
  async getManagedVehicleCount({ state, commit }) {
    if (state.managedVehicleCount !== null) return state.managedVehicleCount;
    const { data } = await StatisticsService.managedVehicleCount();
    commit("setManagedVehicleCount", data);
    return data;
  },
  async getUnManagedVehicleCount({ state, commit }) {
    if (state.unManagedVehicleCount !== null) return state.unManagedVehicleCount;
    const { data } = await ReportService.getUnmanagedReports({ PageSize: 1 });
    commit("setUnManagedVehicleCount", data.TotalResults);
    return data.TotalResults;
  },
  async getUnpaidReportCount({ state, commit }) {
    if (state.unpaidReportCount !== null) return state.unpaidReportCount;
    const { data } = await StatisticsService.unpaidReportCount();
    commit("setUnpaidReportCount", data);
    return data;
  },
  async getUnpaidAmount({ state, commit }) {
    if (state.unpaidAmount !== null) return state.unpaidAmount;
    const { data } = await StatisticsService.unpaidAmount();
    const value = parseFloat(data.toFixed(2));
    commit("setUnpaidAmount", value);
    return value;
  },
  async getAwaitingTransfersCount({ state, commit }) {
    if (state.awaitingTransfersCount !== null) return state.awaitingTransfersCount;
    const { data } = await ReportService.search({
      CanBeTransferred: true,
      IgnoreDoubleFineForTransfer: true,
      IgnoreExceptionsForTransfer: true,
      IsNotMine: true,
      IsPaid: false,
      PageSize: 1,
    });
    commit("setAwaitingTransfersCount", data.TotalResults);
    return data.TotalResults;
  },
  clearCache({ commit }) {
    commit("clearCache");
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
